import { useRef, useState } from 'react'
import { NodeViewContent, NodeViewWrapper, type NodeViewProps } from '@tiptap/react'
import { imageFilesFrom, useUploader } from '../upload'

/**
 * An image with an optional caption.
 *
 * The caption is editable content rather than an attribute, so it can hold a
 * link or emphasis. The three fields below are the ones the real corpus uses:
 * 34 of 35 imported figures carry a width, and dropping it would reflow every
 * screenshot to full width.
 *
 * `alt` gets a warning rather than a placeholder when empty. Every one of the
 * 52 images in the GitBook export had `alt=""`, so the field being empty is the
 * expected state after an import — and the writer is the only person who can
 * fix it.
 *
 * An empty figure offers to take a file. It used to say "paste a URL below",
 * which is not what anybody has: they have a screenshot. The URL field stays,
 * because an image hosted elsewhere is a real case, but it is no longer the only
 * way in.
 */
export function FigureView({ node, updateAttributes, editor }: NodeViewProps) {
  const src = (node.attrs['src'] as string | null) ?? ''
  const alt = (node.attrs['alt'] as string | null) ?? ''
  const width = (node.attrs['width'] as string | null) ?? ''
  const srcDark = (node.attrs['srcDark'] as string | null) ?? ''

  const upload = useUploader()
  const picker = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [over, setOver] = useState(false)

  const take = async (file: File | undefined) => {
    if (!file || !upload) return
    setBusy(true)
    try {
      const result = await upload(file)
      // Only the src. An alt the uploader happens to know is worth keeping, but
      // never worth overwriting one the writer has already typed.
      if (result) updateAttributes({ src: result.url, ...(alt ? {} : { alt: result.altText }) })
    } finally {
      setBusy(false)
    }
  }

  return (
    <NodeViewWrapper className="be-figure" data-sls="figure">
      <div className="be-figure__preview" contentEditable={false}>
        {src ? (
          <img src={src} alt={alt} {...(width ? { width } : {})} />
        ) : (
          <div
            className={`be-figure__drop${over ? ' is-over' : ''}`}
            onDragOver={(e) => {
              if (!upload) return
              e.preventDefault()
              setOver(true)
            }}
            onDragLeave={() => setOver(false)}
            onDrop={(e) => {
              if (!upload) return
              e.preventDefault()
              setOver(false)
              void take(imageFilesFrom(e.dataTransfer)[0])
            }}
          >
            {upload && editor.isEditable ? (
              <>
                <button
                  type="button"
                  className="btn btn--tinted btn--sm"
                  disabled={busy}
                  onClick={() => picker.current?.click()}
                >
                  {busy ? 'Uploading…' : 'Choose a screenshot'}
                </button>
                <span className="be-figure__or">or drop one here, or paste anywhere</span>
                <input
                  ref={picker}
                  type="file"
                  accept="image/*,application/pdf"
                  hidden
                  onChange={(e) => void take(e.target.files?.[0])}
                />
              </>
            ) : (
              <p className="be-figure__empty">No image yet.</p>
            )}
          </div>
        )}
      </div>

      {editor.isEditable && (
        <div className="be-figure__fields" contentEditable={false}>
          <label className="be-figure__field">
            <span>Image URL</span>
            <input
              className="be-inline-input"
              value={src}
              placeholder="/assets/screenshot.png"
              onChange={(e) => updateAttributes({ src: e.target.value || null })}
            />
          </label>
          <label className="be-figure__field">
            <span>Alt text</span>
            <input
              className="be-inline-input"
              value={alt}
              placeholder="What the image shows"
              onChange={(e) => updateAttributes({ alt: e.target.value || null })}
            />
          </label>
          {/* Only offered once there IS an image: a dark twin of nothing is
              a field that asks a question the writer cannot answer yet. An
              import fills it in, which is where most of them come from. */}
          {src && (
            <label className="be-figure__field">
              <span>Dark version</span>
              <input
                className="be-inline-input"
                value={srcDark}
                placeholder="Optional — shown on a dark ground"
                onChange={(e) => updateAttributes({ srcDark: e.target.value || null })}
              />
            </label>
          )}
          <label className="be-figure__field be-figure__field--narrow">
            <span>Width</span>
            <input
              className="be-inline-input"
              value={width}
              placeholder="auto"
              inputMode="numeric"
              onChange={(e) => updateAttributes({ width: e.target.value || null })}
            />
          </label>
        </div>
      )}

      {!alt && (
        <p className="be-figure__warn" contentEditable={false}>
          No alt text. A screen reader will announce nothing for this image.
        </p>
      )}

      <NodeViewContent className="be-figure__caption" />
    </NodeViewWrapper>
  )
}
